import { useState } from 'react';
import { Plus, Ticket } from 'lucide-react';

import type { AdminStore } from '@/client/types/stores';
import type { StoreVoucher, StoreVoucherCreate } from '@/client/types/stores/parity';
import { useStoreVouchersQuery } from '@/client/api/stores/parity.query';
import { useCreateStoreVoucher, useRevokeStoreVoucher } from '@/client/api/stores/parity.mutation';
import { PERMISSIONS } from '@/client/config/permissions';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { Input } from '@/components/ui/input';

import { Pager, Pill, SectionCard, isOn, money, shortDate, ListSkeleton } from './shared';
import { ReasonDialog } from './ReasonDialog';
import { FieldRow } from './settings/controls';

const LIMIT = 20;

/** Voucher types (ff-api `voucher_type`). */
const VOUCHER_TYPES: { vtid: StoreVoucherCreate['vtid']; label: string; unit: string }[] = [
  { vtid: 2, label: 'Fixed discount', unit: 'GHS off' },
  { vtid: 3, label: 'Percentage discount', unit: '% off' },
  { vtid: 1, label: 'Payment credit', unit: 'GHS credit' },
  { vtid: 4, label: 'Access pass', unit: '' },
];

const voucherState = (v: StoreVoucher): { label: string; tone: string } => {
  if (isOn(v.revoked)) return { label: 'revoked', tone: 'bg-gray-100 text-gray-600' };
  if (isOn(v.redeemed)) return { label: 'redeemed', tone: 'bg-blue-100 text-blue-800' };
  if (v.date_valid_until && new Date(v.date_valid_until) < new Date()) return { label: 'expired', tone: 'bg-gray-100 text-gray-600' };
  return { label: 'active', tone: 'bg-green-100 text-green-800' };
};

const valueLabel = (v: StoreVoucher) => {
  const n = Number(v.value);
  if (v.vtid === 3) return `${n}% off`;
  if (v.vtid === 4) return 'Access pass';
  return v.vtid === 1 ? `${money(n)} credit` : `${money(n)} off`;
};

const CreateDialog: React.FC<{ storeid: number; onClose: () => void }> = ({ storeid, onClose }) => {
  const create = useCreateStoreVoucher();
  const [form, setForm] = useState({ vtid: 2 as StoreVoucherCreate['vtid'], value: '', code: '', min_purchase_amount: '', max_redemptions: '', date_valid_until: '' });
  const set = (k: Exclude<keyof typeof form, 'vtid'>, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const value = Number(form.value);
  const valid = form.vtid === 4 || (form.value.trim() !== '' && value > 0 && (form.vtid !== 3 || value <= 100));
  const unit = VOUCHER_TYPES.find((t) => t.vtid === form.vtid)?.unit;

  const payload = (): StoreVoucherCreate => ({
    vtid: form.vtid,
    value: form.vtid === 4 ? 0 : value,
    ...(form.code.trim() ? { code: form.code.trim().toUpperCase() } : {}),
    ...(form.min_purchase_amount ? { min_purchase_amount: Number(form.min_purchase_amount) } : {}),
    ...(form.max_redemptions ? { max_redemptions: Number(form.max_redemptions) } : {}),
    ...(form.date_valid_until ? { date_valid_until: `${form.date_valid_until}T23:59:59` } : {}),
  });

  return (
    <ReasonDialog
      open
      title="Create a store voucher"
      description="Issued by this store, as if the seller had created it."
      confirmLabel="Create voucher"
      reasonRequired
      loading={create.isPending}
      confirmDisabled={!valid}
      onConfirm={(reason) => create.mutate({ storeid, reason, ...payload() }, { onSuccess: onClose })}
      onClose={onClose}
    >
      <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 px-3">
        <FieldRow label="Type">
          <select value={form.vtid} onChange={(e) => setForm((f) => ({ ...f, vtid: Number(e.target.value) as StoreVoucherCreate['vtid'] }))} className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm">
            {VOUCHER_TYPES.map((t) => <option key={t.vtid} value={t.vtid}>{t.label}</option>)}
          </select>
        </FieldRow>
        {form.vtid !== 4 && (
          <FieldRow label="Value" hint={unit}>
            <Input type="number" min={0} value={form.value} onChange={(e) => set('value', e.target.value)} className="w-28" />
          </FieldRow>
        )}
        <FieldRow label="Code" hint="Leave blank to generate one.">
          <Input value={form.code} onChange={(e) => set('code', e.target.value)} className="w-40 uppercase" />
        </FieldRow>
        <FieldRow label="Minimum spend" hint="GHS, optional">
          <Input type="number" min={0} value={form.min_purchase_amount} onChange={(e) => set('min_purchase_amount', e.target.value)} className="w-28" />
        </FieldRow>
        <FieldRow label="Max uses" hint="Blank = single use">
          <Input type="number" min={1} value={form.max_redemptions} onChange={(e) => set('max_redemptions', e.target.value)} className="w-28" />
        </FieldRow>
        <FieldRow label="Valid until">
          <Input type="date" value={form.date_valid_until} onChange={(e) => set('date_valid_until', e.target.value)} className="w-40" />
        </FieldRow>
      </div>
    </ReasonDialog>
  );
};

/** Vouchers issued by this store; create and revoke with `admin.store.vouchers`. */
export const VouchersTab: React.FC<{ store: AdminStore }> = ({ store }) => {
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<StoreVoucher | null>(null);
  const revoke = useRevokeStoreVoucher();

  const { data, isLoading } = useStoreVouchersQuery(store.storeid, { status: status || undefined, limit: LIMIT, offset });
  const vouchers = data?.vouchers ?? [];

  return (
    <SectionCard
      bare
      description="Discount codes and credits issued by this store."
      actions={
        <div className="flex items-center gap-2">
          <select value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs" aria-label="Status">
            <option value="">All</option>
            {['active', 'redeemed', 'revoked', 'expired'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <PermissionButton code={PERMISSIONS.store.vouchers} size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> New voucher
          </PermissionButton>
        </div>
      }
    >
      {isLoading ? (
        <ListSkeleton rows={5} />
      ) : vouchers.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-400">No vouchers.</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {vouchers.map((v) => {
            const state = voucherState(v);
            return (
              <div key={v.vid} className="flex items-center gap-3 py-2.5">
                <Ticket className="h-4 w-4 flex-shrink-0 text-gray-400" />
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-sm font-semibold text-gray-900">{v.code}</p>
                  <p className="text-xs text-gray-500">
                    {valueLabel(v)}
                    {v.min_purchase_amount ? ` · min ${money(Number(v.min_purchase_amount))}` : ''}
                    {v.date_valid_until ? ` · until ${shortDate(v.date_valid_until)}` : ''}
                  </p>
                </div>
                <Pill tone={state.tone}>{state.label}</Pill>
                {state.label === 'active' && (
                  <PermissionButton code={PERMISSIONS.store.vouchers} size="sm" variant="outline" onClick={() => setRevoking(v)}>
                    Revoke
                  </PermissionButton>
                )}
              </div>
            );
          })}
        </div>
      )}
      <Pager total={data?.pagination.total ?? 0} limit={LIMIT} offset={offset} onChange={setOffset} />

      {creating && <CreateDialog storeid={store.storeid} onClose={() => setCreating(false)} />}
      {revoking && (
        <ReasonDialog
          open
          title={`Revoke ${revoking.code}`}
          description="The code stops working immediately. Redeemed uses are not reversed."
          confirmLabel="Revoke"
          destructive
          reasonRequired
          loading={revoke.isPending}
          onConfirm={(reason) =>
            revoke.mutate({ storeid: store.storeid, vid: revoking.vid, reason }, { onSuccess: () => setRevoking(null) })
          }
          onClose={() => setRevoking(null)}
        />
      )}
    </SectionCard>
  );
};
